import { MIDDLE_C, StemType, TICKS_PER_LOOP } from '@arpeggi/shared'
import JZZ from 'jzz'
import { useEffect } from 'react'
import { Arp2Utils } from '../../client/SongUtils'
import { useTreeState } from '../../stores/Root'
import { IDrumStemStore } from '../../stores/song/stem/DrumStemStore'
import { IStemStore } from '../../stores/song/StemStore'

const NOTE_ON_HEX = 0x90
const NOTE_OFF_HEX = 0x80
const COMMAND_BYTE_MASK_HEX = 0xf0

interface PressedMIDINote {
  startTicks: number
  visibleTicks: number
  velocity: number
}

const getDrumIndex = (semitone: number): number => {
  return semitone - MIDDLE_C
}

/**
 * A hook for enabling the midi api in arpeggi
 *
 * Midi transfers data (notes, velocity, duration, sysex etc.) on 16 possible channels per input/output.
 * The current implementation listens for midi data on all channels on all inputs
 *
 * data: Uint8Array -> 3 x bytes -> [command/channel, note, velocity].
 * command/channel: first byte is command and second is channel
 * e.g.
 * 0x91 = 0x90 is note on, 0x01 is midi channel 2 (channels are 0 indexed)
 * 0x80 = 0x80 is note off, 0x00 midi channel 1
 */
export const useMidi = (stem: IStemStore) => {
  const { studio } = useTreeState()
  const { playSampleWithEffects } = studio
  const pressedMidiNotes: Record<number, PressedMIDINote> = {}

  useEffect(() => {
    if (stem.type() !== StemType.INSTRUMENT && stem.type() !== StemType.DRUMS) return
    const inputs = []

    const setup = async () => {
      await JZZ().or('Cannot start MIDI engine')
      JZZ.info().inputs.forEach(async (inp) => {
        try {
          const input = await JZZ().openMidiIn(inp.id)
          await input.connect(onMIDIMessage)
          inputs.push(input)
        } catch (err) {}
      })
    }

    setup()

    return () => inputs.forEach((input) => input.disconnect())
  }, [stem.type(), stem.uniqueId])

  const onMIDIMessage = (data: number[]) => {
    const semitone = data[1]
    const velocity = 127 // data[2] // TODO: Add this in when we have velocity support in the studio

    // Masking the channel to enable notes an any channel
    if ((data[0] & COMMAND_BYTE_MASK_HEX) === NOTE_ON_HEX) {
      studio.setPlayingNoteOn(semitone)
      pressedMidiNotes[semitone] = {
        startTicks: studio.playheadStartPositionTicks + studio.playheadElapsedTicks,
        visibleTicks: studio.playheadVisiblePositionInTicks,
        velocity
      }

      // playing the note
      if (stem.type() === StemType.INSTRUMENT) {
        playSampleWithEffects(
          stem.getCurrentSample(),
          stem.getStemGain(),
          semitone,
          undefined,
          true
        )
      } else if (stem.type() === StemType.DRUMS) {
        const drumIndex = getDrumIndex(semitone)
        const drumStem = stem as IDrumStemStore

        // When you play a midi note, middle C represents index 0 in the
        // drum sample array, with every note after representing the next index
        // so if the index is less than 0 or greater than number of drum samples,
        // then it can't play anything
        if (!(drumIndex < 0 || drumIndex >= drumStem.drumSamples.length)) {
          playSampleWithEffects(
            drumStem.drumSamples[drumIndex],
            stem.getStemGain(),
            Arp2Utils.getMiddleC(),
            undefined,
            true
          )
        }
      }
    }

    // Masking the channel to enable notes an any channel
    if ((data[0] & COMMAND_BYTE_MASK_HEX) === NOTE_OFF_HEX) {
      studio.setPlayingNoteOff(semitone)
      const pressedNoteData = pressedMidiNotes[semitone]
      if (!pressedNoteData) return

      const noteDuration =
        studio.playheadStartPositionTicks + studio.playheadElapsedTicks - pressedNoteData.startTicks
      const loop = stem.getLoopAtSectionOrNull(studio.currentSection)

      // recording the notes to the mobx store
      if (studio.isPlaying && studio.isRecording && loop) {
        if (stem.type() === StemType.INSTRUMENT) {
          const melodicLoop = loop.melodicLoop
          melodicLoop.notes.addNote({
            start: pressedNoteData.visibleTicks % TICKS_PER_LOOP,
            duration: noteDuration,
            note: semitone,
            velocity
          })
        } else if (stem.type() === StemType.DRUMS) {
          const drumIndex = getDrumIndex(semitone)
          const drumSubLoop = loop.drumLoop.subLoops[drumIndex]

          // We're still quantizing drums because they need to be quantized to work with the drum seq
          // TODO: remove drum quantization and move from drum seq -> drum piano roll
          const resolutionLength = Arp2Utils.getLengthOfRes(drumSubLoop.settings.resolution)
          const startTicks =
            ((((pressedNoteData.visibleTicks % TICKS_PER_LOOP) / TICKS_PER_LOOP) *
              resolutionLength) /
              resolutionLength) *
            TICKS_PER_LOOP
          drumSubLoop.notes.addNote({
            start: startTicks,
            duration: noteDuration,
            note: MIDDLE_C,
            velocity
          })
        }
      }

      pressedMidiNotes[semitone] = null
    }
  }
}
